import { ContentType, UserAccountType, Visibility } from "authory-api-types/dist/enums";
import { ContentWithSharesResponse, UserResponse } from "authory-api-types/dist/types";
import React from "react";
import { V3ContentRenderer } from "../../components/ContentRenderer";
import { V3Loader } from "../../components/Loader";
import { OGTags, SEO_TYPE } from "../../components/OGTags";
import { V3ContentLargeWrapper } from "../../components/ReusableStyledComponents/V3ContentLargeWrapper";
import { V3PublicSizer } from "../../components/ReusableStyledComponents/V3PublicSizer";
import { V3HeadlineH1 } from "../../components/Typography";
import { ContactUserFormik } from "../../components/V3ContactUser";
import { V3PublicAuthorHeader } from "../../components/V3PublicAuthorHeader";
import { V3SharesReportDesktop } from "../../components/V3SharesReport";
import { VerticalSpacing } from "../../components/VerticalSpacing";
import { ContentLoaderWrapper, ProgressInderterminate, ProgressMaterialize, PublicContentWrapper } from "./ContentDetailsPublicPageLayout.styles";
import { V3ContentNavbar } from "../../components/V3ContentNavbar";
import Script from "next/script";
import { ContentCollectionsPanel } from "../../components/ContentCollectionsPanel";
import { BannerDetails } from "../../components/BannerDetails";
import { InlineLink } from "../../components/ReusableStyledComponents/InlineLink";
import { contentHasDefaultPreview } from "../../utils/articleHasCustomPreview";
import { V3EmptyState } from "../../components/V3EmptyStateScreen/V3EmptyStateScreen";
import { V3BodyGrey48 } from "../../components/ReusableStyledComponents/V3BodyGrey48";
import { ShareSettingsTooltip } from "../../components/ReusableStyledComponents/ShareSettingsTooltip";

interface ContentDetailsPublicPageLayoutProps {
    author: UserResponse;
    content?: ContentWithSharesResponse;
    isPrivateView?: boolean;
    onContactUserSubmit: (data: ContactUserFormik) => Promise<void>;
    addOrRemoveItemManuallyFromCollection?: (add: boolean, articleSlug: string, collectionSlug: string) => Promise<void>;
    onCustomPreviewOpen?: () => void;
    importInProgress?: boolean;
}

export const ContentDetailsPublicPageLayout = ({
    author,
    content,
    isPrivateView = false,
    importInProgress = false,
    onContactUserSubmit,
    addOrRemoveItemManuallyFromCollection,
    onCustomPreviewOpen
}: ContentDetailsPublicPageLayoutProps) => {
    const isBusinessParent = author.type === UserAccountType.BusinessParent;
    const isArticle = content?.article.type === ContentType.Article;

    const hasCustomPreview = contentHasDefaultPreview(content?.article);

    return (
        <V3PublicSizer isPrivateView={isPrivateView}>
            {author && !isPrivateView && <OGTags user={author} target={SEO_TYPE.ARTICLE} article={content?.article} />}
            {!isPrivateView && <V3PublicAuthorHeader user={author} onContactUserSubmit={onContactUserSubmit} />}
            {isPrivateView && content && "sum" in content.shares && isArticle && <V3SharesReportDesktop shares={content.shares} />}
            <V3ContentLargeWrapper>
                {
                    <PublicContentWrapper>
                        <VerticalSpacing top={42}>
                            {content ? (
                                <>
                                    <V3ContentNavbar
                                        content={content.article}
                                        isBusinessParent={isBusinessParent}
                                        isPrivateView={isPrivateView || false}
                                        viewOriginal={!content.article.publishedOnAuthory && !!content.article.originalUrl}
                                    />
                                    {isPrivateView && (
                                        <VerticalSpacing bottom={24}>
                                            <ContentCollectionsPanel
                                                contentCollections={content.article.collections}
                                                allCollections={author.collections}
                                                addOrRemoveItemManuallyFromCollection={(add, collectionSlug) => {
                                                    if (addOrRemoveItemManuallyFromCollection)
                                                        addOrRemoveItemManuallyFromCollection(add, content.article.canonicalSlug, collectionSlug);
                                                }}
                                            />
                                        </VerticalSpacing>
                                    )}
                                    {isPrivateView && (
                                        <VerticalSpacing bottom={12}>
                                            <BannerDetails useYellow>
                                                {content.article.effectiveVisibility === Visibility.Preview ? (
                                                    <>This content item links to the original instead of the Authory backup.</>
                                                ) : (
                                                    <>This content item links to the Authory backup instead of the original piece.</>
                                                )}{" "}
                                                <InlineLink
                                                    href="https://authory.com/help/en/articles/9667500-how-does-the-preview-of-content-items-work#h_02b3c1cdb5"
                                                    target="_blank"
                                                >
                                                    Learn more
                                                </InlineLink>
                                            </BannerDetails>
                                        </VerticalSpacing>
                                    )}
                                    {isPrivateView && hasCustomPreview && (
                                        <VerticalSpacing bottom={24}>
                                            <BannerDetails>
                                                This content item has a{" "}
                                                <InlineLink onClick={() => onCustomPreviewOpen && onCustomPreviewOpen()}>
                                                    {" "}
                                                    custom preview headline{" "}
                                                </InlineLink>{" "}
                                                set.{" "}
                                                <InlineLink
                                                    href="https://authory.com/help/en/articles/9667500-how-does-the-preview-of-content-items-work#h_cc824a2476"
                                                    target="_blank"
                                                >
                                                    Learn more
                                                </InlineLink>
                                            </BannerDetails>
                                        </VerticalSpacing>
                                    )}
                                    <VerticalSpacing bottom={24}>
                                        <V3HeadlineH1>{content.article.title}</V3HeadlineH1>
                                    </VerticalSpacing>
                                    {importInProgress ? (
                                        <VerticalSpacing top={60}>
                                            <V3EmptyState
                                                customPadding="40px 20px"
                                                title={isPrivateView ? "Backup creation in progress" : "Page creation in progress"}
                                                customDescriptionElem="div"
                                                description={
                                                    <VerticalSpacing top={35}>
                                                        {isPrivateView
                                                            ? "A copy of the original article is currently being created. This will usually take around 30 seconds."
                                                            : "The content for this page is currently being imported. This will usually take around 30 seconds."}
                                                    </VerticalSpacing>
                                                }
                                                svgIcon={
                                                    <VerticalSpacing bottom={-25}>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="24"
                                                            height="24"
                                                            fill="none"
                                                            stroke="#ABB0BA"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <circle cx="12" cy="12" r="10" />
                                                            <path d="M12 6v6l4 2" />
                                                        </svg>
                                                    </VerticalSpacing>
                                                }
                                                maxWidth={600}
                                            >
                                                <VerticalSpacing top={-14} bottom={30}>
                                                    <V3BodyGrey48>
                                                        {isPrivateView ? (
                                                            "You can leave this page and return later anytime."
                                                        ) : (
                                                            <>
                                                                <strong>Please note:</strong> You'll need to refresh this page to see any updates.
                                                            </>
                                                        )}
                                                    </V3BodyGrey48>
                                                </VerticalSpacing>
                                                <ProgressMaterialize>
                                                    <ProgressInderterminate />
                                                </ProgressMaterialize>
                                                {isPrivateView && (
                                                    <VerticalSpacing top={30}>
                                                        <V3BodyGrey48>
                                                            <strong>Good to know:</strong> By default, the preview of an article on your portfolio
                                                            will forward visitors to the original piece. However, in case the original is not
                                                            available anymore, you can change the settings to forward your visitors to the Authory
                                                            copy instead. <ShareSettingsTooltip text="Here is how" />
                                                        </V3BodyGrey48>
                                                    </VerticalSpacing>
                                                )}
                                            </V3EmptyState>
                                        </VerticalSpacing>
                                    ) : (
                                        <VerticalSpacing bottom={120}>
                                            <V3ContentRenderer content={content.article.contentDetail} />
                                        </VerticalSpacing>
                                    )}
                                </>
                            ) : (
                                <ContentLoaderWrapper>
                                    <V3Loader />
                                </ContentLoaderWrapper>
                            )}
                        </VerticalSpacing>
                    </PublicContentWrapper>
                }
            </V3ContentLargeWrapper>
            {!isPrivateView && <Script src="https://www.recaptcha.net/recaptcha/api.js?render=explicit" strategy="lazyOnload" />}
        </V3PublicSizer>
    );
};
